import { HttpErrorResponse, HttpParams, HttpStatusCode } from '@angular/common/http';
import { EventEmitter, inject, Injectable, signal } from '@angular/core';
import { EmployeeFormModel, ServiceWorkerModel } from '@client-models/employee-form.model';
import { DEFAULT_TIMEZONE } from '@constants/date.constants';
import { LOCAL_STORAGE_CONSTANT } from '@constants/localstorage.constant';
import { loginDbModel } from '@db-models/login-db.model';
import { MfaDbModel, MfaLocalStorageModel, MfaVerifyResetDbModel } from '@db-models/mfa.models';
import { AuthRole, ServiceWorkerDbModel, ThemePreference, WorkerCloneBody, WorkerDbModel, WorkerFilterModel, WorkerUpdateFieldsBody } from '@db-models/worker-db.model';
import { environment } from '@environments/environment';
import { TimezoneService } from '@feature-services/timezone.service';
import { AlertToastrService } from '@util-services/alert-toastr.service';
import { CryptoService } from '@util-services/crypto.service';
import { HelperService } from '@util-services/helper.service';
import { HttpClientService } from '@util-services/http-client.service';
import { LocalStorageService } from '@util-services/local-storage.service';
import { LoggerService } from '@util-services/logger.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, share, shareReplay, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class WorkerService {
  onWorkerSelectionChanged = new EventEmitter<WorkerDbModel[]>();
  onWorkerDetailsChanged = new EventEmitter<WorkerDbModel>();

  private httpClientService = inject(HttpClientService);
  private localstorageService = inject(LocalStorageService);
  private cryptoService = inject(CryptoService);
  private helperService = inject(HelperService);
  private alertToasterService = inject(AlertToastrService);
  private timezoneService = inject(TimezoneService);

  mfaToken: string;

  // In-flight request dedup for /workers/short and /workers/short-calendar.
  // Multiple components mounting in the same tick share the same HTTP response;
  // entries clear on response so subsequent calls re-fetch fresh data.
  private inFlightShortRequests = new Map<string, Observable<WorkerDbModel[]>>();

  importWorkersExcel(file: File, partnerId: number): Observable<WorkerDbModel[]> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('partner_id', partnerId + '');
    return this.httpClientService.post('workers/import', formData, {});
  }

  getWorkersByPagination(param: { limit: number, offset: number, sort?: string, direction?: string }): Observable<{ workers: WorkerDbModel[], total_count: number }> {
    const params = new HttpParams()
      .set('limit', param.limit + '')
      .set('offset', param.offset + '')
      .set('sort', (param?.sort || ''))
      .set('direction', (param?.direction || ''));

    return this.httpClientService.get('workers', { params: params }).pipe(
      switchMap((response: { workers: WorkerDbModel[], total_count: number }) => {
        const workers = response.workers || [];

        workers.forEach(worker => {
          if (worker?.services_workers?.length) {
            worker.services_workers = worker.services_workers.filter(service => service.appointment_service !== null);
          }
        });

        return of({ workers, total_count: response.total_count || 0 });
      })
    );
  }

  searchWorkers(body: WorkerFilterModel, limit: number, offset: number): Observable<WorkerDbModel[]> {
    const params = new HttpParams().set('limit', limit + '').set('offset', offset + '');
    return this.httpClientService.post(`workers/search`, body, { params }).pipe(
      switchMap((response: { workers: WorkerDbModel[], total_count: number }) => of(response.workers || []))
    );
  }

  searchWorkersPaginated(body: WorkerFilterModel, limit: number, offset: number): Observable<{ workers: WorkerDbModel[], total_count: number }> {
    const params = new HttpParams().set('limit', limit + '').set('offset', offset + '');
    return this.httpClientService.post(`workers/search`, body, { params });
  }

  getWorkers(): Observable<WorkerDbModel[]> {
    const workers: WorkerDbModel[] = this.getWorkersFromLocalStorage();
    if (workers) {
      workers.forEach(worker => {
        if (worker?.services_workers?.length) {
          worker.services_workers = worker.services_workers.filter(service => service.appointment_service !== null);
        }
      });

      return of(workers);
    }

    return this.workerList$.pipe(switchMap((worker) => of(worker).pipe(take(1))));
  }

  workerList$ = new Observable((observer) => {
    // Simulate an HTTP request
    this.httpClientService.get('workers', {}).pipe(
      switchMap((workers: WorkerDbModel[]) => {
        workers?.forEach(worker => {
          !worker.calendar_color && (worker.calendar_color = this.helperService.getRandomColor());
          if (worker?.services_workers?.length) {
            worker.services_workers = worker.services_workers.filter(service => service.appointment_service !== null);
          }
        });

        return of(workers);
      })
    ).subscribe({
      next: workers => {
        observer.next(workers);
        observer.complete();
      },
      error: (error: HttpErrorResponse) => {
        observer.error(error);
        observer.complete();
      }
    });
  }).pipe(
    // Share the response and replay the last emitted value to new subscribers
    share()
  ) as Observable<WorkerDbModel[]>;

  // Decrypting the stored worker is AES over the whole blob, and the dashboard reads it
  // thousands of times per page load (the header re-reads it on every change-detection
  // cycle). For a worker with many service assignments the blob is several hundred KB, so
  // that adds up to minutes of blocked main thread. Cache the parsed worker against the
  // exact ciphertext it was decrypted from: every writer goes through localStorage, and
  // AES.encrypt salts randomly, so any write yields a different string and the cache misses
  // on its own - there is no invalidation hook to forget to call.
  //
  // Callers still get their OWN object, exactly as they did when this decrypted every time.
  // That is deliberate: several call sites mutate the returned worker in place before
  // persisting it, and handing them all the same instance would let a mutation that is never
  // persisted (a failed PUT, an early return) leak into every other reader for the rest of
  // the session. The copy is shallow, which is enough - nothing mutates a nested field, and
  // on non-prod builds the cached instance is deep-frozen so a nested mutation throws.
  private cachedWorkerCipher: string = null;
  private cachedWorker: WorkerDbModel = null;

  private deepFreeze<T>(value: T): T {
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
      Object.values(value).forEach(nested => this.deepFreeze(nested));
      Object.freeze(value);
    }
    return value;
  }

  // Bumped on every write/clear of the stored worker so a computed() can key on it.
  private loggedInWorkerRevisionState = signal(0);
  loggedInWorkerRevision = this.loggedInWorkerRevisionState.asReadonly();

  constructor() {
    // A write from another tab never passes through this tab's persistLoggedInWorker().
    window.addEventListener('storage', storageEvent => {
      const changedKey = storageEvent.key;
      if (changedKey === null || changedKey.startsWith(LOCAL_STORAGE_CONSTANT.LOGGED_IN_WORKER)) {
        this.loggedInWorkerRevisionState.update(revision => revision + 1);
      }
    });
  }

  getLoggedInWorkerFromLocalStorage(): WorkerDbModel {
    const encryptedWorker = this.localstorageService.get(LOCAL_STORAGE_CONSTANT.LOGGED_IN_WORKER);
    if (encryptedWorker) {
      if (encryptedWorker !== this.cachedWorkerCipher) {
        const decryptedWorkers = this.cryptoService.decryptValue(encryptedWorker);
        const worker = JSON.parse(decryptedWorkers) as WorkerDbModel;
        this.cachedWorker = environment.production ? worker : this.deepFreeze(worker);
        this.cachedWorkerCipher = encryptedWorker;
      }
      return { ...this.cachedWorker };
    } else {
      this.cachedWorkerCipher = null;
      this.cachedWorker = null;
      return null;
    }
  }

  // The only writer of LOGGED_IN_WORKER, so no write can skip the revision bump.
  persistLoggedInWorker(worker: WorkerDbModel): void {
    const encryptedString = this.cryptoService.encryptValue(JSON.stringify(worker));
    this.localstorageService.set(LOCAL_STORAGE_CONSTANT.LOGGED_IN_WORKER, encryptedString);
    this.loggedInWorkerRevisionState.update(revision => revision + 1);
  }

  removeLoggedInWorker(): void {
    this.localstorageService.remove(LOCAL_STORAGE_CONSTANT.LOGGED_IN_WORKER);
    this.clearLoggedInWorkerCache();
  }

  // Called from logout, handlePartnerSwitch, handleAuthCallback's error branch and
  // removeLoggedInWorker(): this root singleton outlives the navigation to the login screen, so
  // without it the previous user's decrypted worker stays reachable until the tab closes.
  clearLoggedInWorkerCache(): void {
    this.cachedWorkerCipher = null;
    this.cachedWorker = null;
    this.loggedInWorkerRevisionState.update(revision => revision + 1);
  }

  setLoggedInUserInLocalStorage(workerUuid = ''): Observable<WorkerDbModel> {
    return this.filterWorkers(workerUuid).pipe(
      switchMap(workers => {
        let foundedWorker: WorkerDbModel = null;
        if (workers?.length === 1) {
          foundedWorker = workers[0];
        } else if (workers?.length > 1) {
          foundedWorker = workers.find(worker => worker.uuid === workerUuid);
        }

        if (foundedWorker) {
          this.persistLoggedInWorker(foundedWorker);
        }
        this.timezoneService.setTimezoneInLocalStorage(foundedWorker?.timezone || DEFAULT_TIMEZONE);

        return of(foundedWorker);
      }),
      catchError(() => {
        return of(null);
      })
    );
  }

  filterWorkers(uuid: string) {
    const body = {
      uuid: {
        value: uuid,
        operator: '='
      }
    };
    // The stored logged-in worker never needs services_workers. A backend without the flag
    // rejects the unknown query key with a 400, so that one case retries without it.
    const params = new HttpParams().set('exclude_services_workers', '1');
    return this.httpClientService.post(`workers/filter`, body, { params }).pipe(
      catchError((error: HttpErrorResponse) => error?.status === HttpStatusCode.BadRequest
        ? this.httpClientService.post(`workers/filter`, body, {})
        : throwError(() => error)),
      switchMap((workers: WorkerDbModel[]) => workers ? of(workers) : of([])),
      catchError(() => of([]))
    );
  }

  filterWorkerById(id: number): Observable<WorkerDbModel> {
    const body = {
      id: {
        value: id,
        operator: '='
      }
    };
    return this.httpClientService.post(`workers/filter`, body, {}).pipe(
      map((workers: WorkerDbModel[]) => workers?.[0] || null),
      catchError(() => of(null))
    );
  }

  getWorkerById(uuid: string): Observable<WorkerDbModel> {
    return this.httpClientService.get(`workers/${uuid}`, {}).pipe(
      switchMap((worker: WorkerDbModel) => {
        if (worker?.services_workers?.length) {
          worker.services_workers = worker.services_workers.filter(service => service.appointment_service !== null);
        }
        return of(worker);
      })
    );
  }

  saveWorker(employeeFormModel: EmployeeFormModel): Observable<WorkerDbModel> {
    this.clearWorkersFromLocalStorage();
    if (employeeFormModel.id) {
      return this.editWorker(employeeFormModel);
    } else {
      return this.createWorker(employeeFormModel);
    }
  }

  createWorker(employeeFormModel: EmployeeFormModel): Observable<WorkerDbModel> {
    employeeFormModel['error_toast_in_interceptor'] = false;
    return this.httpClientService.post('workers', employeeFormModel, {});
  }

  editWorker(employeeFormModel: EmployeeFormModel): Observable<WorkerDbModel> {
    const loggedInWorker = this.getLoggedInWorkerFromLocalStorage();
    if (employeeFormModel?.uuid) {
      return this.httpClientService.put(`workers/${employeeFormModel.uuid}`, employeeFormModel, {}).pipe(
        switchMap(worker => {
          if (loggedInWorker.uuid === worker.uuid) {
            this.timezoneService.setTimezoneInLocalStorage(worker?.timezone || DEFAULT_TIMEZONE);
            this.persistLoggedInWorker(worker);
          }
          return of(worker);
        })
      );
    } else {
      LoggerService.warn('Failed to edit worker as uuid is missing', employeeFormModel);
      return null;
    }
  }

  setLanguage(workerId: number, language: string): Observable<WorkerDbModel> {
    // Normalize regional English variants (en_GB, en_AU, en_CA) to en_US for backend storage.
    // The backend only supports base languages; regional variants are a UI-only concern stored in localStorage.
    const backendLang = language?.startsWith('en_') && language !== 'en_US' ? 'en_US' : language;
    const body = {
      worker_id: workerId,
      lang_identifier: backendLang
    };
    return this.httpClientService.post(`workers/language`, body, {}).pipe(
      switchMap((result: { worker: WorkerDbModel }) => {
        return of(result.worker);
      })
    );
  }

  /**
   * Persist the worker's dark/light theme preference server-side so the choice
   * follows them across browsers/devices. Dedicated lightweight endpoint — a full
   * worker PUT on every theme toggle would be heavy. Partner-scoped on the backend.
   */
  updateWorkerTheme(workerUuid: string, themePreference: ThemePreference): Observable<{ theme_preference: string }> {
    return this.httpClientService.put(`workers/${workerUuid}/theme`, { theme_preference: themePreference }, {});
  }

  /**
   * Patch the cached logged-in worker's `theme_preference` in localStorage so a
   * same-session page reload reflects the just-made choice. Without this, the reload
   * path (`app.component` → `ColorSchemeService.hydrateFromWorker`) re-reads the STALE
   * cached value and silently reverts the theme until the next full login. Applied
   * optimistically (not gated on the server PUT) so the local choice always survives a
   * refresh even if the server sync fails/offline — the same localStorage re-write the
   * generic `editWorker` PUT already performs for the logged-in worker.
   */
  patchLoggedInWorkerTheme(themePreference: ThemePreference): void {
    const worker = this.getLoggedInWorkerFromLocalStorage();
    if (!worker?.uuid) {
      return;
    }
    worker.theme_preference = themePreference;
    this.persistLoggedInWorker(worker);
  }

  saveServiceWorkers(serviceWorkerModel: ServiceWorkerModel): Observable<{ skills: ServiceWorkerDbModel[] }> {
    return this.httpClientService.post(`services_workers`, serviceWorkerModel, {});
  }

  deleteWorker(uuid: string): Observable<{ success: boolean }> {
    this.clearWorkersFromLocalStorage();
    return this.httpClientService.delete(`workers/${uuid}`, { body: { error_toast_in_interceptor: false } });
  }

  getCalendarWorkersList(): Observable<WorkerDbModel[]> {
    const cacheKey = 'short-calendar';
    const inFlight = this.inFlightShortRequests.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    const request$ = this.httpClientService.get(`workers/short-calendar`, {}).pipe(
      switchMap(workers => {
        if (workers?.length) {
          for (const worker of workers) {
            if (!worker.calendar_color) {
              worker.calendar_color = this.helperService.getRandomColor();
            }
          }
        }
        return of(workers);
      }),
      finalize(() => this.inFlightShortRequests.delete(cacheKey)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.inFlightShortRequests.set(cacheKey, request$);
    return request$;
  }

  getLimitedWorkersList(limit: number = null, offset: number = null, selfFirst: boolean = false): Observable<WorkerDbModel[]> {
    let params = new HttpParams();

    if (limit !== null && offset !== null) {
      params = params
        .set('limit', limit + '')
        .set('offset', offset + '');
    }

    if (selfFirst) {
      params = params.set('self_first', 'true');
    }

    if (limit === null && offset === null) {
      const workers = this.getShortWorkersFromLocalStorage();
      if (workers) {
        return of(workers);
      }
    }

    const cacheKey = `short:${limit}:${offset}:${selfFirst}`;
    const inFlight = this.inFlightShortRequests.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    const request$ = this.httpClientService.get(`workers/short`, { params }).pipe(
      switchMap(workers => {
        if (workers?.length) {
          for (const worker of workers) {
            if (!worker.calendar_color) {
              worker.calendar_color = this.helperService.getRandomColor();
            }
          }
          (limit === null && offset === null) && this.setShortWorkersInLocalStorage(workers);
        }
        return of(workers);
      }),
      finalize(() => this.inFlightShortRequests.delete(cacheKey)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.inFlightShortRequests.set(cacheKey, request$);
    return request$;
  }

  updateSingleWorkerInLocalStorage(worker: WorkerDbModel) {
    const workers = this.getWorkersFromLocalStorage();
    if (workers) {
      const foundIndex: number = workers.findIndex(x => x.id === worker.id);
      workers[foundIndex] = worker;
      this.setWorkersInLocalStorage(workers);
    }
  }

  setShortWorkersInLocalStorage(workers: WorkerDbModel[]): void {
    const encryptedString = this.cryptoService.encryptValue(
      JSON.stringify(workers)
    );
    this.localstorageService.set(LOCAL_STORAGE_CONSTANT.SHORT_WORKER_LIST_KEY, encryptedString);
  }

  getShortWorkersFromLocalStorage(): WorkerDbModel[] {
    const encryptedWorkers: string = this.localstorageService.get(LOCAL_STORAGE_CONSTANT.SHORT_WORKER_LIST_KEY);
    if (encryptedWorkers) {
      const decryptedWorkers: string = this.cryptoService.decryptValue(encryptedWorkers);
      const workers: WorkerDbModel[] = JSON.parse(decryptedWorkers);
      return workers;
    } else {
      return null;
    }
  }

  setWorkersInLocalStorage(workers: WorkerDbModel[]): void {
    const encryptedString = this.cryptoService.encryptValue(
      JSON.stringify(workers)
    );
    this.localstorageService.set(LOCAL_STORAGE_CONSTANT.WORKERS_KEY, encryptedString);
  }

  getWorkersFromLocalStorage(): WorkerDbModel[] {
    const encryptedWorkers: string = this.localstorageService.get(LOCAL_STORAGE_CONSTANT.WORKERS_KEY);
    if (encryptedWorkers) {
      const decryptedWorkers: string = this.cryptoService.decryptValue(encryptedWorkers);
      const workers: WorkerDbModel[] = JSON.parse(decryptedWorkers);
      return workers;
    } else {
      return null;
    }
  }

  createClientWorkerFormModel(workerDbModel: WorkerDbModel): EmployeeFormModel {
    const employeeFormModel: EmployeeFormModel = new EmployeeFormModel();
    employeeFormModel.id = workerDbModel.id;
    employeeFormModel.prename = workerDbModel.prename;
    employeeFormModel.lastname = workerDbModel.lastname;
    employeeFormModel.username = workerDbModel.username;
    employeeFormModel.booking_label = workerDbModel.booking_label;
    employeeFormModel.description = workerDbModel.description;
    employeeFormModel.email = workerDbModel.email;
    employeeFormModel.job_title = workerDbModel.job_title;
    employeeFormModel.conference_url = workerDbModel.conference_url;
    employeeFormModel.store_id = workerDbModel.store_id;
    employeeFormModel.bookable = !!(workerDbModel.bookable === 1);
    employeeFormModel.show_in_pro_calendar = !!(workerDbModel.show_in_pro_calendar === 1);
    employeeFormModel.calendar_color = workerDbModel.calendar_color;
    return employeeFormModel;
  }

  clearWorkersFromLocalStorage() {
    this.localstorageService.remove(LOCAL_STORAGE_CONSTANT.WORKERS_KEY);
    this.localstorageService.remove(LOCAL_STORAGE_CONSTANT.SHORT_WORKER_LIST_KEY);
  }

  uploadAvatar(file: File, relativePath: string, uuid: string): Observable<{ worker: WorkerDbModel }> {
    const formData: FormData = new FormData();
    relativePath = relativePath.replace('.', Math.floor(Math.random() * 10000) + '.');
    formData.append('avatar', file, relativePath);
    return this.httpClientService.post(`workers/avatar/${uuid}`, formData, {});
  }

  deleteWorkerSkill(id: number): Observable<{ success: boolean }> {
    return this.httpClientService.delete(`services_workers/${id}`, {});
  }

  updateWorker(worker: WorkerDbModel): Observable<WorkerDbModel> {
    return this.httpClientService.put(`workers/${worker.uuid}`, worker, {});
  }

  resetWorkerPassword(email: string): Observable<{ success: boolean }> {
    const body: { [key: string]: string } = { email };
    return this.httpClientService.post(`workers/reset`, body, {});
  }

  changeWorkerPassword(body: {
    worker_id: number,
    old_password: string,
    password: string,
    password_repeat: string
  }): Observable<{ success: boolean }> {
    return this.httpClientService.post(`workers/password`, body, {});
  }

  createNewWorkerPassword(body: {
    worker_id: number,
    token: string,
    password: string,
    password_repeat: string
  }): Observable<{ success: boolean }> {
    return this.httpClientService.post(`workers/new_password`, body, {});
  }

  loginAsResource(partner_uuid: string, worker_uuid: string): Observable<{ success: boolean }> {
    const body = { partner_uuid, worker_uuid };
    return this.httpClientService.post(`workers/partnerAuth`, body, {});
  }

  setAnonymousUserInLocalStorage(user: { name: string, email: string }) {
    const encryptedString = this.cryptoService.encryptValue(
      JSON.stringify(user)
    );
    this.localstorageService.set(LOCAL_STORAGE_CONSTANT.ANONYMOUS_USER_CREDENTIALS, encryptedString);
  }

  getAnonymousUserFromLocalStorage(): { name: string, email: string } {
    const encrypted: string = this.localstorageService.get(LOCAL_STORAGE_CONSTANT.ANONYMOUS_USER_CREDENTIALS);
    if (encrypted) {
      const decryptedWorkers: string = this.cryptoService.decryptValue(encrypted);
      const user: { name: string, email: string } = JSON.parse(decryptedWorkers);
      return user;
    } else {
      return null;
    }
  }

  getWorkerAvatarFromLocalStorage(): string {
    return this.localstorageService.get(LOCAL_STORAGE_CONSTANT.AVATAR_KEY);
  }

  getRoles(): Observable<AuthRole[]> {
    return this.httpClientService.get(`roles`, {}).pipe(
      map(roles => roles.sort((a, b) => a.name.localeCompare(b.name)))
    );
  }

  loginAsReseller(partnerId: number): Observable<{ success: boolean }> {
    const body: { partner_id: number } = { partner_id: partnerId };
    return this.httpClientService.post(`workers/reseller_auth`, body, {});
  }

  resetResellerAdmin(): Observable<{ success: boolean }> {
    return this.httpClientService.post(`workers/reseller_logout`, {}, {});
  }

  enableWorkerMfa(workerUuid: string): Observable<MfaDbModel> {
    return this.httpClientService.authServicePost(
      'workers/generate_mfa_secret',
      { error_toast_in_interceptor: false, worker_uuid: workerUuid },
      {}
    );
  }

  resetWorkerMfa(code: string, workerUuid: string): Observable<MfaVerifyResetDbModel> {
    return this.httpClientService.authServiceDelete(
      'workers/mfa',
      { body: { code, worker_uuid: workerUuid } }
    );
  }

  verifyWorkeMfa(param: { code: string; token: string }): Observable<loginDbModel> {
    return this.httpClientService.authServicePost(
      'auth/verify_mfa',
      param,
      {}
    );
  }

  addWorkerMfa(code: string): Observable<MfaVerifyResetDbModel> {
    return this.httpClientService.authServicePost(
      'workers/mfa',
      { code, token: this.mfaToken },
      {}
    );
  }

  newVerifyWorkeMfa(code: string): Observable<MfaVerifyResetDbModel> {
    return this.httpClientService.authServicePost(
      'auth/verify-mfa-code',
      { code, reset: false },
      {}
    );
  }

  setMfaVerificationDetailsInLocalStorage(): void {
    const mfaData: MfaLocalStorageModel = {
      verified: true,
      date: new Date().toISOString()
    };
    const encryptedString = this.cryptoService.encryptValue(
      JSON.stringify(mfaData)
    );
    this.localstorageService.set(LOCAL_STORAGE_CONSTANT.MFA, encryptedString);
  }

  getMfaVerificationDetailsInLocalStorage(): MfaLocalStorageModel | null {
    const encrypted = this.localstorageService.get(LOCAL_STORAGE_CONSTANT.MFA);
    if (encrypted) {
      const decryptedMfa = this.cryptoService.decryptValue(encrypted);
      const mfaData: {
        verified: boolean,
        date: string
      } = JSON.parse(decryptedMfa);
      return mfaData;
    }
    return null;
  }

  getSupportUsers(): Observable<WorkerDbModel[]> {
    const body = { is_support_user: { value: 1, operator: '=' } };
    return this.httpClientService.post(`workers/filter`, body, {}).pipe(
      switchMap((workers: WorkerDbModel[]) => of(workers ? workers : [])),
      catchError(() => of([]))
    );
  }

  cloneWorker(body: WorkerCloneBody): Observable<WorkerDbModel> {
    return this.httpClientService.post(`workers/clone_worker`, body, {});
  }

  updateFields(body: WorkerUpdateFieldsBody, uuid: string): Observable<any> {
    return this.httpClientService.put(`workers/${uuid}/fields`, body, {});
  }

  importOffice365Calendar(uuid: string): Observable<{ success: boolean, message?: string }> {
    return this.httpClientService.post(`workers/import_office365_calendar`, { uuid }, {});
  }

  importOffice365Teams(uuid: string): Observable<{ success: boolean, message?: string }> {
    return this.httpClientService.post(`workers/import_office365_teams`, { uuid }, {});
  }

  exportFile(filters: WorkerFilterModel): Observable<{ file: string, type: string }> {
    const body = { error_toast_in_interceptor: false };
    body.error_toast_in_interceptor = false;

    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        body[key] = filters[key];
      }
    });

    return this.httpClientService.exportServicePost('workers/export', body, {})
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error?.error?.error) {
            if (error.error.error === 'ReferredPartnerMismatchException') {
              this.alertToasterService.showError('general.requested_partner_not_found');
            } else if (error.error.error === 'WorkerHasPendingExportException') {
              this.alertToasterService.showError('general.export_pending_job_message');
            } else {
              this.alertToasterService.showError('webhook_stat_component.webhook_endpoint_error');
            }
          } else {
            this.alertToasterService.showError('webhook_stat_component.webhook_endpoint_error');
          }

          return throwError(() => error);
        })
      );
  }
}
